import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { diffWords } from 'diff';
import { KbArticle, KbVersion } from '../types';
import {
  VersionPanel,
  VersionHeader,
  VersionList,
  VersionItem,
  VersionNumber,
  VersionMeta,
  ActionButton,
  IconBtn,
  ContentArea,
  ContentHeader,
  ContentActions,
  ContentBody,
  MarkdownContent,
  EmptyState,
} from '../styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

interface Props {
  article: KbArticle;
  versions: KbVersion[];
  onClose: () => void;
  onFetchVersionContent: (articleId: number, versionId: number) => Promise<KbVersion>;
  onRestore: (articleId: number, versionId: number) => void;
}

const buildDiffMarkdown = (oldText: string, newText: string): string => {
  const changes = diffWords(oldText, newText);
  let result = '';
  for (const part of changes) {
    if (part.added) {
      // Wrap additions in HTML with green background
      result += `<ins style="background:#d4edda;text-decoration:none;">${part.value}</ins>`;
    } else if (part.removed) {
      // Wrap deletions in HTML with red background + strikethrough
      result += `<del style="background:#f8d7da;text-decoration:line-through;">${part.value}</del>`;
    } else {
      result += part.value;
    }
  }
  return result;
};

const VersionHistory: React.FC<Props> = ({ article, versions, onClose, onFetchVersionContent, onRestore }) => {
  const [selectedVersion, setSelectedVersion] = useState<KbVersion | null>(null);
  const [versionContent, setVersionContent] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedVersion(null);
    setVersionContent('');
  }, [article.id]);

  const handleSelectVersion = async (version: KbVersion) => {
    setSelectedVersion(version);
    setLoading(true);
    const full = await onFetchVersionContent(article.id, version.id);
    if (full && full.content !== undefined) {
      setVersionContent(full.content);
    }
    setLoading(false);
  };

  const diffHtml = useMemo(() => {
    if (!selectedVersion || !versionContent) return '';
    return buildDiffMarkdown(versionContent, article.content);
  }, [versionContent, article.content, selectedVersion]);

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <ContentArea>
        {selectedVersion ? (
          <>
            <ContentHeader>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                Changes: v{selectedVersion.versionNumber} → Current
                <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
                  by {selectedVersion.createdBy} on {selectedVersion.createdAt}
                </span>
              </div>
              <ContentActions>
                {!article.protected && (
                  <ActionButton onClick={() => onRestore(article.id, selectedVersion.id)}>
                    Restore this version
                  </ActionButton>
                )}
              </ContentActions>
            </ContentHeader>
            <ContentBody>
              {loading ? (
                <div>Loading...</div>
              ) : (
                <MarkdownContent>
                  <ReactMarkdown
                    plugins={[remarkGfm, remarkBreaks as any]}
                    rehypePlugins={[rehypeRaw as any]}
                  >
                    {diffHtml}
                  </ReactMarkdown>
                </MarkdownContent>
              )}
            </ContentBody>
          </>
        ) : (
          <EmptyState>Select a version to see changes</EmptyState>
        )}
      </ContentArea>
      <VersionPanel>
        <VersionHeader>
          <h3>Version History</h3>
          <IconBtn onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </IconBtn>
        </VersionHeader>
        <VersionList>
          {versions.length === 0 && (
            <div style={{ padding: 16, color: '#999', fontSize: 13 }}>No previous versions</div>
          )}
          {versions.map((version) => (
            <VersionItem
              key={version.id}
              $selected={selectedVersion?.id === version.id}
              onClick={() => handleSelectVersion(version)}
            >
              <VersionNumber>Version {version.versionNumber}</VersionNumber>
              <VersionMeta>
                {version.createdBy} - {version.createdAt}
              </VersionMeta>
            </VersionItem>
          ))}
        </VersionList>
      </VersionPanel>
    </div>
  );
};

export default VersionHistory;
